import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Hotel, Star, Wifi, Car, Utensils, Dumbbell, Info } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import DestinationAutocomplete from '../components/DestinationAutocomplete';
import BookingConfirmation from '../components/BookingConfirmation';
import BookingModal from '../components/BookingModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const amenityIcons = {
  WiFi: Wifi,
  Pool: () => <span className="text-xs">🏊</span>,
  Spa: () => <span className="text-xs">💆</span>,
  Restaurant: Utensils,
  Gym: Dumbbell,
  Bar: () => <span className="text-xs">🍸</span>,
  Parking: Car,
  AC: () => <span className="text-xs">❄️</span>,
  TV: () => <span className="text-xs">📺</span>,
};

export default function HotelsPage() {
  const [searchData, setSearchData] = useState({
    city: '',
    check_in: '',
    check_out: '',
    guests: 1
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchData.city || !searchData.check_in || !searchData.check_out) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      setLoading(true);
      const res = await axios.post(`${API}/hotels/search`, searchData);
      setResults(res.data);
      setSearched(true);
    } catch (err) {
      toast.error('Error searching hotels');
    } finally {
      setLoading(false);
    }
  };

  const calculateNights = () => {
    if (!searchData.check_in || !searchData.check_out) return 1;
    const start = new Date(searchData.check_in);
    const end = new Date(searchData.check_out);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return diff > 0 ? diff : 1;
  };

  const openBookingModal = (hotel) => {
    setSelectedHotel(hotel);
    setShowBookingModal(true);
  };

  const handleBookingSubmit = async (customerData) => {
    if (!selectedHotel) return;

    const nights = calculateNights();
    const totalPrice = selectedHotel.price_per_night * nights;

    try {
      setBookingLoading(true);
      const res = await axios.post(`${API}/bookings/create`, {
        booking_type: 'hotel',
        customer_name: customerData.name,
        customer_mobile: customerData.mobile,
        customer_email: customerData.email || '',
        city: searchData.city,
        check_in: searchData.check_in,
        check_out: searchData.check_out,
        travelers: searchData.guests,
        provider_name: selectedHotel.name,
        class_type: `${selectedHotel.stars} Star`,
        price: totalPrice
      });
      
      setShowBookingModal(false);
      setBookingDetails({
        bookingId: res.data.booking_id,
        name: selectedHotel.name,
        details: `${selectedHotel.stars} Star • ${searchData.city} • ${nights} Night(s)`,
        date: `${searchData.check_in} to ${searchData.check_out}`,
        travelers: searchData.guests,
        price: totalPrice
      });
      setShowConfirmation(true);
    } catch (err) {
      toast.error('Error creating booking');
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pt-20" data-testid="hotels-page">
      {/* Hero */}
      <section className="relative h-64 md:h-80 bg-gradient-to-r from-purple-600 to-purple-800 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1920"
            alt="Hotels"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 mb-4"
          >
            <Hotel className="w-12 h-12 text-white" />
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-medium text-white mb-4"
          >
            Book Hotels
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-purple-100"
          >
            Find perfect stays at best prices
          </motion.p>
        </div>
      </section>

      {/* Search Form */}
      <section className="max-w-4xl mx-auto px-6 -mt-12 relative z-20">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          onSubmit={handleSearch}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
          data-testid="hotel-search-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <DestinationAutocomplete
              label="City / Destination"
              value={searchData.city}
              onChange={(val) => setSearchData({ ...searchData, city: val })}
              placeholder="Goa"
            />
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Check In</label>
              <input
                type="date"
                value={searchData.check_in}
                onChange={(e) => setSearchData({ ...searchData, check_in: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none"
                data-testid="check-in"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Check Out</label>
              <input
                type="date"
                value={searchData.check_out}
                onChange={(e) => setSearchData({ ...searchData, check_out: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none"
                data-testid="check-out"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 block">Guests</label>
              <select
                value={searchData.guests}
                onChange={(e) => setSearchData({ ...searchData, guests: parseInt(e.target.value) })}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none bg-white"
                data-testid="guests"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-center">
            <Button
              type="submit"
              disabled={loading}
              className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-12 py-6 text-lg font-medium"
              data-testid="search-hotels-btn"
            >
              {loading ? 'Searching...' : 'Search Hotels'}
            </Button>
          </div>
        </motion.form>
      </section>

      {/* Discount Banner */}
      <section className="max-w-4xl mx-auto px-6 mt-8">
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 flex items-center gap-3">
          <Info className="w-5 h-5 text-purple-600 flex-shrink-0" />
          <p className="text-sm text-purple-800">
            <span className="font-semibold">Exclusive Hotel Deals!</span> Book through ID4U Travels for discounted rates and free upgrades.
          </p>
        </div>
      </section>

      {/* Results */}
      {searched && (
        <section className="max-w-5xl mx-auto px-6 py-12" data-testid="hotel-results">
          <h2 className="text-2xl font-semibold text-slate-900 mb-6">
            {results.length > 0 ? `${results.length} Hotels Found` : 'No Hotels Found'}
          </h2>
          <div className="space-y-6">
            {results.map((hotel, index) => (
              <motion.div
                key={hotel.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow border border-slate-100"
                data-testid={`hotel-result-${index}`}
              >
                <div className="flex flex-col md:flex-row">
                  <div className="w-full md:w-72 h-48 md:h-auto relative overflow-hidden">
                    <img
                      src={hotel.image}
                      alt={hotel.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-xl font-semibold text-slate-900">{hotel.name}</h3>
                          <div className="flex items-center gap-0.5">
                            {[...Array(hotel.stars)].map((_, i) => (
                              <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-slate-500">{hotel.city}</p>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-100 rounded-lg">
                        <Star className="w-4 h-4 text-green-600 fill-green-600" />
                        <span className="font-semibold text-green-700">{hotel.rating}</span>
                        <span className="text-xs text-green-600">({hotel.reviews})</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {hotel.amenities?.map((amenity, i) => {
                        const Icon = amenityIcons[amenity] || (() => null);
                        return (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-md text-xs text-slate-600">
                            <Icon className="w-3 h-3" />
                            {amenity}
                          </span>
                        );
                      })}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <div>
                        <p className="text-2xl font-bold text-purple-600">₹{hotel.price_per_night?.toLocaleString()}</p>
                        <p className="text-xs text-slate-500">per night</p>
                      </div>
                      <Button
                        onClick={() => openBookingModal(hotel)}
                        className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                        data-testid={`book-hotel-${index}`}
                      >
                        Book Now
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Booking Modal */}
      <AnimatePresence>
        {showBookingModal && (
          <BookingModal
            isOpen={showBookingModal}
            onClose={() => setShowBookingModal(false)}
            onSubmit={handleBookingSubmit}
            title="Book Your Hotel"
            loading={bookingLoading}
          />
        )}
      </AnimatePresence>

      {/* Booking Confirmation Modal */}
      <AnimatePresence>
        {showConfirmation && (
          <BookingConfirmation
            bookingType="hotel"
            bookingDetails={bookingDetails}
            onClose={() => setShowConfirmation(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
